import { useMemo } from 'react'

import { useIsMutating } from '@tanstack/react-query'

import { AgGridWidget, CustomAgChart } from '@fortem-monorepo/react-ag'
import { Button, ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { chartEmailMutationKey } from '@/react-query/mutations/accrued-interests'
import { useAccruedBalanceChart } from '@/react-query/queries/accrued-interests'

import { useAccruedInterestsContext } from '../AccruedInterests.context'
import { getBalanceChartOptions } from '../balance-chart'
import { getChartDataRows } from '../chart-data'
import { chartDataColumns } from './chart-data-columns'
import { centeredColumn } from './columns'

export const AccruedBalanceChartTab = () => {
  const { params, chartInstrumentId } = useAccruedInterestsContext()
  const query = useAccruedBalanceChart({ fund_name: params.fund_name, instrument_id: chartInstrumentId })
  const options = useMemo(() => (query.data ? getBalanceChartOptions(query.data) : null), [query.data])
  const rows = useMemo(() => (query.data ? getChartDataRows(query.data) : []), [query.data])
  const emailPending = useIsMutating({ mutationKey: chartEmailMutationKey }) > 0
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-3 pb-3">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 py-2">
        <div>
          <h3 className="text-sm font-semibold">Saved accrued interest balance history</h3>
          <p className="text-xs text-muted-foreground">
            All saved dates; trade balances are summed per day. Yellow vertical lines mark scheduled coupon payments.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!chartInstrumentId || query.isFetching || emailPending}
            onClick={() => query.refetch()}
          >
            Refresh chart
          </Button>
        </div>
      </div>
      {(!params.fund_name || !chartInstrumentId) && (
        <p className="p-3 text-sm text-muted-foreground">Select a fund and ISIN to view its saved balance history.</p>
      )}
      {query.isFetching && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading chart…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-destructive">
          Unable to load balance history. {getErrorMessage(query.error)}
        </p>
      )}
      {query.data && !query.data.points.length && (
        <p className="text-sm text-muted-foreground">No saved balances for this selection.</p>
      )}
      {options && (
        <ResizablePanelGroup direction="vertical" className="min-h-0 flex-1">
          <ResizablePanel defaultSize={60} minSize={25} className="min-h-0">
            <CustomAgChart
              options={options}
              className="h-full min-h-0 w-full"
              aria-label="Accrued interest balance history with coupon and trade dates"
            />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={40} minSize={25} className="flex min-h-0 flex-col overflow-hidden">
            <AgGridWidget
              query={query}
              widget={{ exposed: true, className: 'min-h-0 flex-1' }}
              widgetHeader={{ title: 'Chart datapoints — 50 rows per page', className: 'shrink-0' }}
              widgetContent={{ className: 'min-h-0 flex-1 overflow-hidden' }}
              grid={{
                rowData: rows,
                columnDefs: chartDataColumns,
                defaultColDef: centeredColumn,
                domLayout: 'normal',
                pagination: true,
                paginationPageSize: 50,
                paginationPageSizeSelector: false,
                getRowId: ({ data }) => data.id,
              }}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
      {query.data && (
        <p className="shrink-0 pt-2 text-xs text-muted-foreground">
          ▲ Green: buys. ▲ Red: sells. Triangles mark UTC execution dates; their height indicates Buy or Sell, not
          balance size. Only saved trade balances contribute on each date; missing dates are not filled. Unknown amounts
          leave a gap. Hover for exact saved amounts and trade details.
        </p>
      )}
    </div>
  )
}
